import backendCredits from '@mxai/credits-rules/backend-credits.json';

/**
 * 视频积分计算工具
 * 前后端共享的视频积分计算逻辑
 */

/** 视频分辨率对应的每秒积分消耗 */
const VIDEO_RESOLUTION_COST_PER_SECOND: Record<string, number> = {
  '480p': 1,  // 480p: 1积分/秒
  '720p': 2,  // 720p: 2积分/秒
  '1080p': 4, // 1080p: 4积分/秒
};

type BackendCreditsConfig = {
  more_model?: Record<string, number>;
};

const SEEDANCE2_MODE_ID = '194';

/** Seedance2 兜底规则，避免规则包数据异常时影响现有扣费口径 */
const FALLBACK_SEEDANCE2_MODEL_COST_PER_SECOND: Record<string, number> = {
  'seedance2.0-video-fast-480p': 12,
  'seedance2.0-video-720p': 24,
  'seedance2.0-video-1080p': 60,
};

function getSeedance2RulesFromPackage(): Record<string, number> {
  const config = (backendCredits as Record<string, BackendCreditsConfig>)[SEEDANCE2_MODE_ID];
  const moreModel = config?.more_model ?? {};

  return Object.fromEntries(
    Object.entries(moreModel).filter(
      (entry): entry is [string, number] =>
        entry[0].startsWith('seedance2.0-video-') && typeof entry[1] === 'number',
    ),
  );
}

/** aidraw-api Seedance2 more_model 对应的每秒积分消耗 */
const VIDEO_MODEL_COST_PER_SECOND: Record<string, number> = {
  ...FALLBACK_SEEDANCE2_MODEL_COST_PER_SECOND,
  ...getSeedance2RulesFromPackage(),
};

/**
 * 计算视频生成的积分消耗（按时长和分辨率动态计算）
 * 
 * 规则：
 * - 480p 分辨率：1积分/秒
 * - 720p 分辨率：2积分/秒
 * - 1080p 分辨率：4积分/秒
 * - Seedance2 按 aidraw-api more_model 的模型每秒积分计算
 * - 支持时长：3-12秒
 * 
 * @param duration 视频时长（秒），默认 5 秒
 * @param resolution 分辨率，默认 480p
 * @param modelId 视频模型ID，传入 Seedance2 时优先按模型计费
 * @returns 积分消耗量
 * 
 * @example
 * calculateVideoCreditsCost(5, '480p')  // 5 积分
 * calculateVideoCreditsCost(5, '720p')  // 10 积分
 * calculateVideoCreditsCost(12, '720p') // 24 积分
 * calculateVideoCreditsCost(5, '1080p') // 20 积分
 * calculateVideoCreditsCost(5, '720p', 'seedance2.0-video-720p') // 120 积分
 */
export function calculateVideoCreditsCost(
  duration: number = 5,
  resolution: string = '480p',
  modelId?: string,
): number {
  // 限制时长范围：3-12秒
  const safeDuration = Math.max(3, Math.min(12, duration));
  
  // 获取每秒积分消耗，默认按 480p 计算
  const costPerSecond = getVideoCostPerSecond(resolution, modelId);
  
  return safeDuration * costPerSecond;
}

/**
 * 获取视频分辨率对应的每秒积分消耗
 * @param resolution 分辨率
 * @param modelId 视频模型ID，传入 Seedance2 时优先按模型计费
 * @returns 每秒积分消耗
 */
export function getVideoCostPerSecond(resolution: string, modelId?: string): number {
  if (modelId && VIDEO_MODEL_COST_PER_SECOND[modelId] !== undefined) {
    return VIDEO_MODEL_COST_PER_SECOND[modelId];
  }
  return VIDEO_RESOLUTION_COST_PER_SECOND[resolution] ?? 1;
}

/** 视频积分计算规则说明 */
export const VIDEO_CREDITS_RULES = {
  /** 支持的分辨率及其每秒消耗 */
  resolutions: VIDEO_RESOLUTION_COST_PER_SECOND,
  /** 模型级每秒消耗，来源于 aidraw-api point_rules more_model */
  models: VIDEO_MODEL_COST_PER_SECOND,
  /** 最小时长（秒） */
  minDuration: 3,
  /** 最大时长（秒） */
  maxDuration: 12,
  /** 默认时长（秒） */
  defaultDuration: 5,
  /** 默认分辨率 */
  defaultResolution: '480p',
};
